import { useCallback, useState } from 'react';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { sendAiChatMessage } from '../infra/aiChatRepo';
import type { AiChatMessage } from '../domain/aiTypes';
import { aiChatErrorMessage } from './aiChatErrorMessage';

const SUGGESTED_PROMPTS = [
  '¿Cuánto vendí hoy?',
  '¿A cuánto vende mi producto más vendido?',
  '¿Cuánto le debe el cliente con más deuda?',
  '¿Qué productos tienen poco stock?',
];

// #1463: antes el welcome listaba las 9 capacidades y los chips de sugerencias
// explicaban lo mismo otra vez. Ahora el welcome es una línea y los chips
// muestran ejemplos accionables.
const WELCOME_MESSAGE: AiChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    'Hola, soy el asistente de tu negocio. Te ayudo con ventas, stock, fiados y más. ¿En qué te ayudo hoy?',
};

export function useAiChat() {
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const [messages, setMessages] = useState<AiChatMessage[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  const reset = useCallback(() => {
    setMessages([WELCOME_MESSAGE]);
    setInput('');
    setWarning(null);
  }, []);

  const send = useCallback(
    async (text: string) => {
      if (!text.trim() || !currentBranchId) return;
      setWarning(null);

      const userMessage: AiChatMessage = {
        id: `${Date.now()}-user`,
        role: 'user',
        content: text.trim(),
      };
      const loadingMessage: AiChatMessage = {
        id: `${Date.now()}-assistant`,
        role: 'assistant',
        content: '',
        isLoading: true,
      };
      setMessages((prev) => [...prev, userMessage, loadingMessage]);
      setInput('');
      setLoading(true);

      const history = [...messages, userMessage]
        .filter((m) => !m.isLoading)
        .map((m) => ({ role: m.role, content: m.content }));

      const result = await sendAiChatMessage(currentBranchId, history);
      setLoading(false);

      if (!result.ok || !result.message) {
        const errorMessage = aiChatErrorMessage(result.errorCode);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === loadingMessage.id
              ? { ...m, isLoading: false, content: errorMessage }
              : m
          )
        );
        return;
      }

      setWarning(result.warning ?? null);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingMessage.id
            ? {
                ...m,
                isLoading: false,
                content: result.message!.content,
                toolName: result.toolCalls?.[0]?.name,
              }
            : m
        )
      );
    },
    [currentBranchId, messages]
  );

  const sendSuggested = useCallback(
    (prompt: string) => {
      setInput(prompt);
      return send(prompt);
    },
    [send]
  );

  return {
    messages,
    input,
    setInput,
    loading,
    warning,
    suggested: SUGGESTED_PROMPTS,
    send,
    sendSuggested,
    reset,
  };
}
